import React, { FC, ReactNode, useEffect } from 'react';

import {
  PageNavigation,
  usePageNavigation,
} from '../../hooks/use-page-navigation.hook';
import { useReportStyles } from '../../report.styles';
import { useReportContext } from '../../report.context';
import { Pagination, ThemeProvider, createTheme } from '@mui/material';
import { useGetScalingSize } from '../../report-style.helpers';
import { is } from '@client/helpers/dom.guards';
import { isScrollable } from '@client/helpers/dom.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { IconButton } from '@ui/buttons';

type FullscreenContentProps = {
  children: ReactNode[];
};

/** Fullscreen letterBox is black, so we need to use a dark theme or the components will have the wrong styling */
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const FullscreenNavigation: FC<{
  pageNav: PageNavigation<ReactNode>;
}> = ({ pageNav }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Pagination
        count={pageNav.display.total}
        page={pageNav.display.page}
        onChange={(_, value) => pageNav.goToPage(value)}
        color="primary"
      />
    </div>
  );
};

// Keyboard constants are centralized in `@shared/models/keyboard.model`

export const ReportFullscreenWrapper: FC<FullscreenContentProps> = ({
  children: pages,
}) => {
  const { exitFullscreen } = useReportContext();
  const getScalingSize = useGetScalingSize();
  const { classes } = useReportStyles({ getScalingSize });
  const pageNav = usePageNavigation({ pages: pages });

  // Listen for keyboard events on document.body
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case KeyboardKeys.ESCAPE:
          exitFullscreen?.();
          break;
        case KeyboardKeys.ARROW_LEFT:
          pageNav.previous?.();
          break;
        case KeyboardKeys.ARROW_UP:
          pageNav.previous?.();
          break;
        case KeyboardKeys.ARROW_RIGHT:
          pageNav.next?.();
          break;
        case KeyboardKeys.ARROW_DOWN:
          pageNav.next?.();
          break;
        case KeyboardKeys.SPACE:
          pageNav.next?.();
          break;
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (!is.HTMLElement(event.target)) return;
      if (isScrollable(event.target, 'y')) return; // don't navigate if user tries to scroll something with a scrollbar

      if (event.deltaY > 0) pageNav.next?.();
      else if (event.deltaY < 0) pageNav.previous?.();
    };

    document.body.addEventListener('keydown', onKeyDown);
    document.body.addEventListener('wheel', onWheel);

    return () => {
      document.body.removeEventListener('keydown', onKeyDown);
      document.body.removeEventListener('wheel', onWheel);
    };
  }, [exitFullscreen, pageNav]);

  return (
    <div className={classes.reportFullscreenWrapper}>
      <div className={classes.reportFullscreenHeader}>
        <ThemeProvider theme={darkTheme}>
          <IconButton
            category="mui"
            icon="Close"
            onClick={() => exitFullscreen?.()}
          />
        </ThemeProvider>
      </div>
      <div className={classes.reportFullscreenContent}>{pageNav.current}</div>
      <div className={classes.reportFullscreenFooter}>
        <ThemeProvider theme={darkTheme}>
          <FullscreenNavigation pageNav={pageNav} />
        </ThemeProvider>
      </div>
    </div>
  );
};
